/**
 * compositeSignal.js
 *
 * Aggregates all active indicators into a single −100…+100 score and
 * translates it into a 5-level verdict:
 *   STRONG BUY   > +60
 *   BUY          > +20
 *   NEUTRAL      −20…+20
 *   SELL         < −20
 *   STRONG SELL  < −60
 *
 * Design rules:
 *   • Pure functions only — no React, no I/O, no network.
 *   • Each contributor returns a signed score in −100…+100 (bearish→bullish)
 *     or `null` when it has no readable state. The `null` contributor is
 *     excluded from BOTH numerator and weight-normaliser so users don't
 *     get penalised for feature-flags they don't have on.
 *   • Weights are per-preset (Conservador / Balanceado / Agresivo). Custom
 *     weights (PRO) are a plain object with the same keys.
 *   • The output includes an `entries` breakdown so the UI tooltip can
 *     explain *why* the verdict is what it is — critical for user trust.
 */

// ── Signal contributors ──────────────────────────────────────────────
//
// Each contributor consumes only what it needs; extra props are ignored.
// Every score is bounded to [-100, +100] and negative = bearish.

/**
 * EMA cross: reads the most-recent cross across the 3 pairs the app
 * tracks (9×21, 21×50, 50×200 aka golden/death). Recent bullish crosses
 * push positive; bearish push negative. Older crosses decay with age.
 *
 * @param {Array<{pair:string, dir:"bull"|"bear", idx:number, ts?:number}>} crosses
 * @param {number} candleCount total candles in the array (for age decay)
 * @returns {number|null} −100..+100 or null when nothing to read
 */
export function scoreEMA(crosses, candleCount) {
  if (!Array.isArray(crosses) || crosses.length === 0) return null;
  if (!Number.isFinite(candleCount) || candleCount < 1) return null;

  // Pair weights: golden/death is the strongest signal, 9×21 the fastest.
  const pairWeight = { "50x200": 1.0, "21x50": 0.7, "9x21": 0.5, "9×21": 0.5, "21×50": 0.7, "50×200": 1.0 };

  // Take up to 3 most-recent crosses (one per pair).
  const seen = new Set();
  const recent = [];
  for (let i = crosses.length - 1; i >= 0 && recent.length < 3; i--) {
    const c = crosses[i];
    if (!c || seen.has(c.pair)) continue;
    seen.add(c.pair);
    recent.push(c);
  }

  let numer = 0;
  let denom = 0;
  for (const c of recent) {
    const w = pairWeight[c.pair] ?? 0.5;
    // Age decay — the older the cross the less it matters. Full weight
    // for crosses in the last 5% of candles, linear decay to 0 at 40%.
    const age = candleCount - 1 - c.idx;
    const ageRatio = age / candleCount;
    let decay;
    if (ageRatio <= 0.05) decay = 1;
    else if (ageRatio >= 0.40) decay = 0;
    else decay = 1 - (ageRatio - 0.05) / (0.40 - 0.05);
    const s = c.dir === "bull" ? 100 : c.dir === "bear" ? -100 : 0;
    numer += s * w * decay;
    denom += w;
  }
  if (denom === 0) return null;
  return clamp(numer / denom, -100, 100);
}

/**
 * Stochastic: extreme-zone crosses only (our detector already filters).
 * Bull cross in oversold pushes strongly positive (mean-reversion buy);
 * bear cross in overbought strongly negative. Age-decays same as EMA.
 *
 * @param {Array<{dir:"bull"|"bear", idx:number}>} crosses
 * @param {number} candleCount
 * @returns {number|null}
 */
export function scoreStochastic(crosses, candleCount) {
  if (!Array.isArray(crosses) || crosses.length === 0) return null;
  if (!Number.isFinite(candleCount) || candleCount < 1) return null;

  // Only the LAST cross matters — stochastic is a mean-reversion signal
  // and stacked crosses would double-count momentum.
  const last = crosses[crosses.length - 1];
  const age = candleCount - 1 - last.idx;
  const ageRatio = age / candleCount;
  let decay;
  if (ageRatio <= 0.02) decay = 1;
  else if (ageRatio >= 0.15) decay = 0;
  else decay = 1 - (ageRatio - 0.02) / (0.15 - 0.02);

  const s = last.dir === "bull" ? 100 : last.dir === "bear" ? -100 : 0;
  return clamp(s * decay, -100, 100);
}

/**
 * Cluster proximity: if the live price is within X% of a large cluster
 * we lean towards mean-reversion — clusters below price act as support
 * (bullish bias), clusters above price act as resistance (bearish bias).
 * Strength scales with cluster mass and inversely with distance.
 *
 * @param {Array<{price:number, mass?:number, size?:number, notional_usd?:number}>} clusters
 * @param {number} livePrice
 * @returns {number|null}
 */
export function scoreClusterProximity(clusters, livePrice) {
  if (!Array.isArray(clusters) || clusters.length === 0) return null;
  if (!Number.isFinite(livePrice) || livePrice <= 0) return null;

  // Look at the top-mass clusters within ±3% of price.
  const window = 0.03;
  const nearby = clusters
    .filter((c) => Number.isFinite(c?.price))
    .map((c) => {
      const distPct = Math.abs(c.price - livePrice) / livePrice;
      const mass = Number.isFinite(c.mass) ? c.mass
        : Number.isFinite(c.notional_usd) ? c.notional_usd
        : Number.isFinite(c.size) ? c.size
        : 1;
      return { price: c.price, distPct, mass, side: c.price < livePrice ? "support" : "resistance" };
    })
    .filter((c) => c.distPct <= window)
    .sort((a, b) => (b.mass / Math.max(0.001, b.distPct)) - (a.mass / Math.max(0.001, a.distPct)))
    .slice(0, 3);

  if (nearby.length === 0) return null;

  // Sum signed contributions: support clusters below → +, resistance above → −.
  // Each contribution weighted by 1/(distPct+0.005) so near beats far.
  let sum = 0;
  let weightSum = 0;
  for (const c of nearby) {
    const w = 1 / (c.distPct + 0.005);
    const dir = c.side === "support" ? 1 : -1;
    sum += dir * w * 60; // 60 base intensity for a very-near cluster
    weightSum += w;
  }
  if (weightSum === 0) return null;
  return clamp(sum / weightSum, -100, 100);
}

/**
 * Wyckoff structure badge: `is_ranging` alone means the market is
 * consolidating (neutral bias); `has_recent_event` (spring/utad) tilts
 * the score directionally when we know which one it was.
 *
 * @param {{is_ranging?:boolean, has_recent_event?:boolean, last_event?:string}} badge
 * @returns {number|null}
 */
export function scoreWyckoff(badge) {
  if (!badge || typeof badge !== "object") return null;
  if (!badge.has_recent_event) {
    // Pure ranging with no event ⇒ slight neutral bias (mean-revert-ish).
    return badge.is_ranging ? 0 : null;
  }
  const evt = String(badge.last_event || "").toLowerCase();
  if (evt.includes("spring") || evt.includes("selling_climax") || evt.includes("sc")) return 70;
  if (evt.includes("utad") || evt.includes("upthrust")) return -70;
  if (evt.includes("ar") || evt.includes("automatic_rally")) return 30;
  return badge.is_ranging ? 0 : null;
}

/**
 * Cascade Predictor: score in 0..1 with a direction. High-score down-cascades
 * are strongly bearish; up-cascades strongly bullish. Below 0.3 ⇒ null (noise).
 *
 * @param {{cascade_score?:number, direction?:string, tendencia?:string}} cascade
 * @returns {number|null}
 */
export function scoreCascade(cascade) {
  if (!cascade || typeof cascade !== "object") return null;
  const raw = Number(cascade.cascade_score);
  if (!Number.isFinite(raw)) return null;
  const score = raw > 1 ? raw / 100 : raw; // accept 0..1 or 0..100
  if (score < 0.3) return null;
  const dir = String(cascade.direction || cascade.tendencia || "").toLowerCase();
  const sign = dir.includes("alcista") || dir.includes("bull") || dir === "up" ? 1
             : dir.includes("bajista") || dir.includes("bear") || dir === "down" ? -1
             : 0;
  if (sign === 0) return null;
  return clamp(sign * score * 100, -100, 100);
}

// ── Presets & aggregator ─────────────────────────────────────────────

/**
 * Three fixed presets tuned per user directive:
 *   Conservador  — leans on EMA + Wyckoff (slow, high-confidence signals).
 *   Balanceado   — even mix.
 *   Agresivo     — leans on Stochastic + Cascade (fast, mean-reversion).
 */
export const PRESETS = {
  conservador: { ema: 40, stochastic: 15, cascade: 10, cluster: 20, wyckoff: 15 },
  balanceado:  { ema: 25, stochastic: 20, cascade: 20, cluster: 20, wyckoff: 15 },
  agresivo:    { ema: 15, stochastic: 30, cascade: 30, cluster: 15, wyckoff: 10 },
};

/**
 * Compute the aggregated composite signal.
 *
 * @param {{
 *   emaCrosses?: Array,
 *   stochasticCrosses?: Array,
 *   candleCount?: number,
 *   clusters?: Array,
 *   livePrice?: number,
 *   wyckoffBadge?: object,
 *   cascadeData?: object,
 * }} inputs
 * @param {{preset?:string, customWeights?:object}} [opts]
 * @returns {{
 *   score: number,             // −100..+100
 *   verdict: "STRONG_BUY"|"BUY"|"NEUTRAL"|"SELL"|"STRONG_SELL",
 *   entries: Array<{key:string, score:number, weight:number, weighted:number}>,
 *   preset: string,
 *   activeCount: number,
 * }}
 */
export function computeCompositeSignal(inputs = {}, opts = {}) {
  const preset = opts.preset || "balanceado";
  const weights = opts.customWeights || PRESETS[preset] || PRESETS.balanceado;

  const contributors = {
    ema: scoreEMA(inputs.emaCrosses, inputs.candleCount),
    stochastic: scoreStochastic(inputs.stochasticCrosses, inputs.candleCount),
    cluster: scoreClusterProximity(inputs.clusters, inputs.livePrice),
    wyckoff: scoreWyckoff(inputs.wyckoffBadge),
    cascade: scoreCascade(inputs.cascadeData),
  };

  const entries = [];
  let weightedSum = 0;
  let weightTotal = 0;
  for (const [key, s] of Object.entries(contributors)) {
    if (s === null || !Number.isFinite(s)) continue;
    const w = Number(weights[key]) || 0;
    if (w <= 0) continue;
    const weighted = s * w;
    entries.push({ key, score: s, weight: w, weighted });
    weightedSum += weighted;
    weightTotal += w;
  }

  const score = weightTotal > 0 ? weightedSum / weightTotal : 0;

  // Confidence floor: with fewer than 2 active contributors the composite
  // is not really "composite" — cap intensity so a single-signal outlier
  // can't reach STRONG_BUY / STRONG_SELL. Users see the raw single-signal
  // score in the tooltip; the badge just refuses the extreme verdict.
  const cappedScore = entries.length < 2 ? clamp(score, -40, 40) : score;

  const verdict =
    cappedScore > 60 ? "STRONG_BUY" :
    cappedScore > 20 ? "BUY" :
    cappedScore < -60 ? "STRONG_SELL" :
    cappedScore < -20 ? "SELL" :
    "NEUTRAL";

  return {
    score: Math.round(cappedScore),
    verdict,
    entries,
    preset,
    activeCount: entries.length,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Color/glow palette for the 5 verdicts. Consumed by the badge UI. */
export const VERDICT_STYLE = {
  STRONG_BUY:  { color: "#10b981", glow: "rgba(16,185,129,0.45)", label: "STRONG BUY"  },
  BUY:         { color: "#22c55e", glow: "rgba(34,197,94,0.35)",  label: "BUY"         },
  NEUTRAL:     { color: "#94a3b8", glow: "rgba(148,163,184,0.25)", label: "NEUTRAL"    },
  SELL:        { color: "#f97316", glow: "rgba(249,115,22,0.35)",  label: "SELL"       },
  STRONG_SELL: { color: "#ef4444", glow: "rgba(239,68,68,0.45)",   label: "STRONG SELL" },
};

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

export const __internals = { clamp };
